import { ref, computed } from 'vue'
import { API } from '../api/client'

interface Settings {
  date_format: string
  time_format: string
  fasting_interval: number
}

const DEFAULT_SETTINGS: Settings = {
  date_format: 'MM/DD/YYYY',
  time_format: 'HH:mm',
  fasting_interval: 16,
}

const settings = ref<Settings>({ ...DEFAULT_SETTINGS })
const loading = ref(false)
const error = ref<string | null>(null)

export function useSettings() {
  const isLoaded = computed(() => !!settings.value)

  async function loadSettings() {
    try {
      loading.value = true
      error.value = null
      const data = await API.getSettings()
      settings.value = {
        date_format: data.date_format || DEFAULT_SETTINGS.date_format,
        time_format: data.time_format || DEFAULT_SETTINGS.time_format,
        fasting_interval: data.fasting_interval || DEFAULT_SETTINGS.fasting_interval,
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load settings'
      console.error('Failed to load settings:', err)
    } finally {
      loading.value = false
    }
  }

  async function saveSettings(updates: Partial<Settings>) {
    try {
      loading.value = true
      error.value = null
      const data = await API.updateSettings(updates)
      settings.value = {
        date_format: data.date_format || DEFAULT_SETTINGS.date_format,
        time_format: data.time_format || DEFAULT_SETTINGS.time_format,
        fasting_interval: data.fasting_interval || DEFAULT_SETTINGS.fasting_interval,
      }
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save settings'
      console.error('Failed to save settings:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  function formatDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    const format = settings.value.date_format

    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()

    return format
      .replace('DD', day)
      .replace('MM', month)
      .replace('YYYY', String(year))
  }

  function formatTime(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    const format = settings.value.time_format

    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    const seconds = String(d.getSeconds()).padStart(2, '0')

    return format
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds)
  }

  function formatDateTime(date: Date | string): string {
    return `${formatDate(date)} ${formatTime(date)}`
  }

  function formatDateTimeRelative(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    
    // Normalize to midnight for date comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    
    const timeDiff = dateOnly.getTime() - today.getTime()
    const dayDiff = timeDiff / (1000 * 60 * 60 * 24)
    
    const formattedTime = formatTime(d)
    
    if (dayDiff === 0) {
      return formattedTime
    } else if (dayDiff === 1) {
      return `Tomorrow ${formattedTime}`
    } else if (dayDiff === -1) {
      return `Yesterday ${formattedTime}`
    } else {
      return `${formatDate(d)} ${formattedTime}`
    }
  }

  function formatInterval(hours: number): string {
    if (hours < 1) {
      const minutes = Math.round(hours * 60)
      return `${minutes}m`
    }
    if (Number.isInteger(hours)) {
      return `${hours}h`
    }
    return `${hours.toFixed(1)}h`
  }

  return {
    settings: computed(() => settings.value),
    isLoaded,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    loadSettings,
    saveSettings,
    formatDate,
    formatTime,
    formatDateTime,
    formatDateTimeRelative,
    formatInterval,
  }
}
